import { useSeoMeta } from '@unhead/react';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  useArtstrPhotosInfinite,
  useArtstrLive,
  useGalleryRelays,
  eventToPhotos,
  MARC_PUBKEY,
  type ArtPhoto,
} from '@/hooks/useArtstrPhotos';
import { useAuthor } from '@/hooks/useAuthor';
import { Skeleton } from '@/components/ui/skeleton';
import { AddPhotoField } from '@/components/AddPhotoField';
import { nip19 } from 'nostr-tools';
import { X, ChevronLeft, ChevronRight, ExternalLink, ZoomIn } from 'lucide-react';

const MARC_NPUB = nip19.npubEncode(MARC_PUBKEY);

// Timestamp at component mount — live sub only delivers events newer than this
const LIVE_SINCE = Math.floor(Date.now() / 1000);

// ─── Skeleton ────────────────────────────────────────────────────────────────

function PhotoSkeleton() {
  return (
    <div className="rounded-xl overflow-hidden border border-primary/30 bg-card aspect-square">
      <Skeleton className="w-full h-full" />
    </div>
  );
}

// ─── Photo card ───────────────────────────────────────────────────────────────

function PhotoCard({ photo, onClick }: { photo: ArtPhoto; onClick: () => void }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) return null;

  return (
    <button
      onClick={onClick}
      className="group relative rounded-xl overflow-hidden border border-primary/30 bg-card aspect-square cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-transform duration-200 motion-safe:hover:scale-[1.02]"
      aria-label={photo.alt || photo.title || 'View artwork'}
    >
      {!loaded && (
        <div className="absolute inset-0">
          <Skeleton className="w-full h-full" />
        </div>
      )}
      <img
        src={photo.url}
        alt={photo.alt || photo.title || 'Artwork by Marc'}
        className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        loading="lazy"
      />
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-200 flex items-end pointer-events-none">
        <div className="p-3 w-full translate-y-full group-hover:translate-y-0 transition-transform duration-200 motion-reduce:translate-y-0 motion-reduce:opacity-0 group-hover:motion-reduce:opacity-100">
          {(photo.title || photo.caption) && (
            <p className="text-white text-sm font-medium line-clamp-2 leading-snug">
              {photo.title || photo.caption}
            </p>
          )}
        </div>
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <ZoomIn className="size-5 text-white drop-shadow-lg" />
        </div>
      </div>
    </button>
  );
}

// ─── Lightbox ────────────────────────────────────────────────────────────────

function Lightbox({
  photos,
  index,
  onClose,
  onPrev,
  onNext,
}: {
  photos: ArtPhoto[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const photo = photos[index];
  const [imgLoaded, setImgLoaded] = useState(false);
  const noteId = nip19.noteEncode(photo.event.id);

  useEffect(() => { setImgLoaded(false); }, [index]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const date = new Date(photo.event.created_at * 1000).toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/60 border border-primary/40 text-white hover:bg-primary/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Close"
      >
        <X className="size-5" />
      </button>

      {index > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/60 border border-primary/40 text-white hover:bg-primary/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Previous image"
        >
          <ChevronLeft className="size-6" />
        </button>
      )}

      {index < photos.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/60 border border-primary/40 text-white hover:bg-primary/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Next image"
        >
          <ChevronRight className="size-6" />
        </button>
      )}

      <div
        className="flex flex-col items-center max-w-5xl w-full px-16 max-h-screen py-12 gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative flex-1 min-h-0 w-full flex items-center justify-center">
          {!imgLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="size-12 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
          )}
          <img
            src={photo.url}
            alt={photo.alt || photo.title || 'Artwork'}
            className={`max-h-[70vh] max-w-full object-contain rounded-xl border border-primary/30 shadow-2xl transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
          />
        </div>

        <div className="w-full flex items-start justify-between gap-4 bg-card/80 border border-primary/30 rounded-xl p-4">
          <div className="flex-1 min-w-0">
            {photo.title && <p className="font-semibold text-foreground truncate">{photo.title}</p>}
            {photo.caption && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{photo.caption}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">{date}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground">{index + 1} / {photos.length}</span>
            <a
              href={`https://njump.me/${noteId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg border border-primary/40 text-primary hover:bg-primary/20 transition-colors"
              aria-label="View on Nostr"
            >
              <ExternalLink className="size-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const Index = () => {
  useSeoMeta({
    title: "Marc's Nostr Art Gallery",
    description: 'A curated gallery of #artstr images by Marc on Nostr. Explore original artwork shared on the decentralized web.',
  });

  // Resolve which relays to query: Marc's NIP-65 relay list + fallbacks.
  const relays = useGalleryRelays();

  // Paginated history
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useArtstrPhotosInfinite(relays);

  // Live subscription — receives new events posted after page load
  const livePhotos = useArtstrLive(LIVE_SINCE, relays);

  // Sentinel for infinite scroll
  const { ref: sentinelRef, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten all paginated events → photos, deduplicate
  const historicPhotos = useMemo(() => {
    const seen = new Set<string>();
    const photos: ArtPhoto[] = [];
    for (const page of data?.pages ?? []) {
      // Map of resolved quoted events for this page (quote-repost images)
      const resolved = new Map(page.resolved);
      for (const event of page.events) {
        for (const photo of eventToPhotos(event, resolved)) {
          const key = `${photo.event.id}-${photo.url}`;
          if (!seen.has(key)) {
            seen.add(key);
            photos.push(photo);
          }
        }
      }
    }
    return photos;
  }, [data?.pages]);

  // Deduplicate live photos against historic ones
  const historicKeys = useMemo(() => {
    const s = new Set<string>();
    for (const p of historicPhotos) s.add(`${p.event.id}-${p.url}`);
    return s;
  }, [historicPhotos]);

  const deduplicatedLive = useMemo(
    () => livePhotos.filter((p) => !historicKeys.has(`${p.event.id}-${p.url}`)),
    [livePhotos, historicKeys],
  );

  // Photos added optimistically by the owner via the "Add photo" field.
  const [ownPhotos, setOwnPhotos] = useState<ArtPhoto[]>([]);
  const handlePhotoAdded = useCallback((photo: ArtPhoto) => {
    setOwnPhotos((prev) => [photo, ...prev]);
  }, []);

  // Combined feed: owner-added + live (newest) on top, then historic.
  // Deduplicate the whole thing by event id + url so nothing appears twice
  // once the published events come back through the live subscription/history.
  const allPhotos = useMemo(() => {
    const seen = new Set<string>();
    const out: ArtPhoto[] = [];
    for (const p of [...ownPhotos, ...deduplicatedLive, ...historicPhotos]) {
      const key = `${p.event.id}-${p.url}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(p);
    }
    return out;
  }, [ownPhotos, deduplicatedLive, historicPhotos]);

  const author = useAuthor(MARC_PUBKEY);
  const metadata = author.data?.metadata;

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const openLightbox = useCallback((index: number) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevPhoto = useCallback(() => setLightboxIndex((i) => (i !== null && i > 0 ? i - 1 : i)), []);
  const nextPhoto = useCallback(
    () => setLightboxIndex((i) => (i !== null && i < allPhotos.length - 1 ? i + 1 : i)),
    [allPhotos.length],
  );

  const photoCount = allPhotos.length;
  const newCount = deduplicatedLive.length;

  // Track whether there's a "new photos" banner to show
  const showNewBanner = newCount > 0;

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Header ── */}
      <header className="relative border-b border-primary/30 bg-black/80 backdrop-blur-sm sticky top-0 z-20">
        <div className="container py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {metadata?.picture ? (
              <img
                src={metadata.picture}
                alt={metadata.name ?? 'Marc'}
                className="size-10 rounded-full border-2 border-primary object-cover"
              />
            ) : (
              <div className="size-10 rounded-full border-2 border-primary bg-primary/20" />
            )}
            <div>
              <h1 className="text-lg font-bold leading-tight tracking-tight text-foreground">
                Marc's Nostr Art Gallery
              </h1>
              <p className="text-xs text-muted-foreground">
                <span className="text-primary font-mono">#artstr</span>
                {photoCount > 0 && (
                  <span className="ml-2">{photoCount} {photoCount === 1 ? 'piece' : 'pieces'}</span>
                )}
                {/* Live indicator */}
                <span className="ml-2 inline-flex items-center gap-1">
                  <span className="inline-block size-1.5 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-green-500">live</span>
                </span>
              </p>
            </div>
          </div>
          <a
            href={`https://njump.me/${MARC_NPUB}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs border border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground transition-colors px-3 py-1.5 rounded-lg font-medium"
          >
            View on Nostr
          </a>
        </div>
      </header>

      {/* ── New photos banner ── */}
      {showNewBanner && (
        <div className="bg-primary/10 border-b border-primary/40 py-2 px-4 text-center text-sm text-primary animate-in fade-in slide-in-from-top-2 duration-300">
          ✨ {newCount} new {newCount === 1 ? 'photo' : 'photos'} just arrived live!
        </div>
      )}

      {/* ── Hero ── */}
      <section className="relative isolate overflow-hidden py-16 border-b border-primary/20">
        {metadata?.banner && (
          <img
            src={metadata.banner}
            alt="Gallery banner"
            className="absolute inset-0 w-full h-full object-cover opacity-20 -z-10"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-background -z-10" />
        <div className="container text-center">
          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-primary via-white to-primary bg-clip-text text-transparent pb-2">
            Marc's Art
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mt-3 text-base md:text-lg">
            {metadata?.about
              ? metadata.about
              : 'Original artwork shared on the decentralized Nostr network under the #artstr hashtag.'}
          </p>
          {metadata?.website && (
            <a
              href={metadata.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary hover:underline"
            >
              <ExternalLink className="size-3.5" />
              {metadata.website.replace(/^https?:\/\//, '')}
            </a>
          )}
        </div>
      </section>

      {/* ── Gallery grid ── */}
      <main className="container py-10">

        {/* Owner-only: add a photo by URL */}
        <AddPhotoField onAdded={handlePhotoAdded} relays={relays} />

        {/* Initial loading */}
        {isLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from({ length: 20 }).map((_, i) => <PhotoSkeleton key={i} />)}
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="border border-dashed border-primary/40 rounded-xl py-16 text-center">
            <p className="text-muted-foreground max-w-sm mx-auto">
              Could not load photos from Nostr relays. Check your connection and try refreshing.
            </p>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !isError && photoCount === 0 && (
          <div className="border border-dashed border-primary/40 rounded-xl py-16 text-center">
            <p className="text-muted-foreground max-w-sm mx-auto">
              No <span className="text-primary font-medium">#artstr</span> photos found yet. Check back soon!
            </p>
          </div>
        )}

        {/* Photos */}
        {!isLoading && photoCount > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {allPhotos.map((photo, i) => (
                <PhotoCard
                  key={`${photo.event.id}-${photo.url}`}
                  photo={photo}
                  onClick={() => openLightbox(i)}
                />
              ))}
            </div>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="mt-8 flex justify-center">
              {isFetchingNextPage && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 w-full">
                  {Array.from({ length: 10 }).map((_, i) => <PhotoSkeleton key={i} />)}
                </div>
              )}
              {!hasNextPage && photoCount > 0 && (
                <p className="text-xs text-muted-foreground py-4">
                  All {photoCount} {photoCount === 1 ? 'piece' : 'pieces'} loaded · New photos appear live
                </p>
              )}
            </div>
          </>
        )}
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-primary/20 py-6 mt-8">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>
            Photos by{' '}
            <a
              href={`https://njump.me/${MARC_NPUB}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {metadata?.name ?? 'Marc'}
            </a>
            {' '}on Nostr
            {relays.length > 0 && (
              <span className="ml-1 opacity-70">· querying {relays.length} relays</span>
            )}
          </span>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary transition-colors"
          >
            Vibed with Shakespeare
          </a>
        </div>
      </footer>

      {/* ── Lightbox ── */}
      {lightboxIndex !== null && allPhotos.length > 0 && (
        <Lightbox
          photos={allPhotos}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevPhoto}
          onNext={nextPhoto}
        />
      )}
    </div>
  );
};

export default Index;
